import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, catchError, of, switchMap } from 'rxjs';
import { AdminBrandingCreativesService } from '../../services/admin-branding-creatives.service';
import {
  assetTypeHeading,
  defaultCardBackground,
  downloadFileName,
  formatLabel,
  groupAssetsByType,
  isVideoAsset,
  previewUrl,
  replacementExtensionMatches,
} from './creatives.helpers';
import {
  BrandAsset,
  BrandAssetTab,
  BrandAssetUploadFolder,
  BRAND_ASSET_UPLOAD_FOLDERS,
  CardBackgroundMode,
} from './creatives.types';

@Component({
  selector: 'admin-branding-creatives',
  templateUrl: './creatives.html',
  styleUrl: './creatives.scss',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Creatives implements OnInit {
  private readonly brandingCreativesService = inject(AdminBrandingCreativesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly reload$ = new Subject<void>();

  readonly backgroundModes: readonly CardBackgroundMode[] = ['light', 'dark', 'checkerboard'];
  readonly uploadFolders = BRAND_ASSET_UPLOAD_FOLDERS;

  readonly loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  readonly loadError = signal<string | null>(null);
  readonly tabs = signal<BrandAssetTab[]>([]);
  readonly activeTabId = signal('');
  readonly globalBackground = signal<CardBackgroundMode | null>(null);
  readonly cardBackgroundOverrides = signal<Record<string, CardBackgroundMode>>({});
  readonly copiedKey = signal<string | null>(null);

  readonly uploadFolder = signal<BrandAssetUploadFolder>('shout-agency');
  readonly uploadFileName = signal<string | null>(null);
  readonly uploadState = signal<'idle' | 'uploading' | 'done' | 'error'>('idle');
  readonly uploadError = signal<string | null>(null);
  readonly uploadSuccessKey = signal<string | null>(null);
  readonly pendingDeleteKey = signal<string | null>(null);
  readonly mutatingKey = signal<string | null>(null);
  readonly mutationKind = signal<'delete' | 'replace' | null>(null);
  readonly cardNotice = signal<{ key: string; tone: 'error' | 'success'; message: string } | null>(
    null,
  );
  readonly assetRevisions = signal<Record<string, number>>({});

  private selectedFile: File | null = null;
  private replaceTargetKey: string | null = null;
  private copiedResetTimer: ReturnType<typeof setTimeout> | null = null;
  private fileInputEl: HTMLInputElement | null = null;

  readonly activeTab = computed(() => {
    const id = this.activeTabId();
    const tabs = this.tabs();
    return tabs.find((tab) => tab.id === id) ?? tabs[0] ?? null;
  });

  readonly assetGroups = computed(() => {
    const tab = this.activeTab();
    return tab ? groupAssetsByType(tab.assets) : [];
  });

  readonly formatLabel = formatLabel;
  readonly assetTypeHeading = assetTypeHeading;
  readonly downloadFileName = downloadFileName;
  readonly previewUrl = previewUrl;

  constructor() {
    this.reload$
      .pipe(
        switchMap(() => {
          this.loadingState.set('loading');
          this.loadError.set(null);
          return this.brandingCreativesService.getCreativesLibrary().pipe(
            catchError((err: unknown) => {
              const message =
                err &&
                typeof err === 'object' &&
                'error' in err &&
                err.error &&
                typeof err.error === 'object' &&
                'message' in err.error
                  ? String((err.error as { message: unknown }).message)
                  : 'Failed to load brand assets from S3.';
              this.loadError.set(message);
              this.loadingState.set('error');
              return of(null);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((library) => {
        if (!library) {
          return;
        }
        const tabs = library.tabs.filter((tab) => this.folderForTabId(tab.id) !== null);
        this.tabs.set(tabs);
        const current = this.activeTabId();
        const stillExists = tabs.some((tab) => tab.id === current);
        const nextTabId = stillExists ? current : (tabs[0]?.id ?? '');
        this.activeTabId.set(nextTabId);
        this.syncUploadFolderFromTab(nextTabId);
        this.loadingState.set('loaded');
      });
  }

  ngOnInit(): void {
    this.loadLibrary();
  }

  loadLibrary(): void {
    this.reload$.next();
  }

  tabLabel(tab: BrandAssetTab): string {
    return `${tab.label} (${tab.assets.length})`;
  }

  selectTab(tabId: string): void {
    this.activeTabId.set(tabId);
    this.syncUploadFolderFromTab(tabId);
  }

  onUploadFolderChange(value: string): void {
    const match = this.uploadFolders.find((folder) => folder.prefix === value);
    if (match) {
      this.uploadFolder.set(match.prefix);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.fileInputEl = input;
    const file = input.files?.item(0) ?? null;
    this.selectedFile = file;
    this.uploadFileName.set(file?.name ?? null);
    this.uploadState.set('idle');
    this.uploadError.set(null);
    this.uploadSuccessKey.set(null);
  }

  clearSelectedFile(): void {
    this.selectedFile = null;
    this.uploadFileName.set(null);
    this.uploadState.set('idle');
    this.uploadError.set(null);
    this.uploadSuccessKey.set(null);
    if (this.fileInputEl) {
      this.fileInputEl.value = '';
    }
  }

  async uploadSelectedFile(): Promise<void> {
    const file = this.selectedFile;
    if (!file) {
      this.uploadError.set('Choose an image or video file first.');
      this.uploadState.set('error');
      return;
    }

    const folder = this.uploadFolder();
    this.uploadState.set('uploading');
    this.uploadError.set(null);
    this.uploadSuccessKey.set(null);

    try {
      const result = await this.brandingCreativesService.uploadCreative(folder, file);
      this.selectedFile = null;
      this.uploadFileName.set(null);
      if (this.fileInputEl) {
        this.fileInputEl.value = '';
      }
      this.uploadSuccessKey.set(result.key);
      this.uploadState.set('done');

      const tabId = this.tabIdForFolder(folder);
      if (tabId) {
        this.activeTabId.set(tabId);
      }
      this.loadLibrary();
    } catch (err: unknown) {
      this.uploadError.set(this.readErrorMessage(err, 'Upload failed.'));
      this.uploadState.set('error');
    }
  }

  private readErrorMessage(err: unknown, fallback: string): string {
    if (!err || typeof err !== 'object') {
      return fallback;
    }

    const httpErr = err as {
      message?: string;
      error?: { message?: string | string[] };
      status?: number;
      statusText?: string;
    };

    const nested = httpErr.error?.message;
    if (Array.isArray(nested) && nested.length > 0) {
      return nested.join(', ');
    }
    if (typeof nested === 'string' && nested.trim()) {
      return nested;
    }
    if (typeof httpErr.message === 'string' && httpErr.message.trim() && !httpErr.message.startsWith('Http failure')) {
      return httpErr.message;
    }
    if (httpErr.status && httpErr.statusText) {
      return `${httpErr.status} ${httpErr.statusText}`;
    }
    return fallback;
  }

  private syncUploadFolderFromTab(tabId: string): void {
    const folder = this.folderForTabId(tabId);
    if (folder) {
      this.uploadFolder.set(folder);
    }
  }

  private folderForTabId(tabId: string): BrandAssetUploadFolder | null {
    const map: Record<string, BrandAssetUploadFolder> = {
      agency: 'shout-agency',
      main: 'shout-main',
      plus: 'shout-plus',
      studio: 'shout-studio',
      socials: 'socials',
      affiliate: 'affiliate',
      other: 'other',
    };
    return map[tabId] ?? null;
  }

  private tabIdForFolder(folder: BrandAssetUploadFolder): string | null {
    const map: Record<BrandAssetUploadFolder, string> = {
      'shout-agency': 'agency',
      'shout-main': 'main',
      'shout-plus': 'plus',
      'shout-studio': 'studio',
      socials: 'socials',
      affiliate: 'affiliate',
      other: 'other',
    };
    return map[folder] ?? null;
  }

  isTabSelected(tabId: string): boolean {
    return this.activeTabId() === tabId;
  }

  getTabClass(tabId: string): string {
    return this.isTabSelected(tabId)
      ? 'border-zinc-900 bg-zinc-900 text-white dark:border-zinc-100 dark:bg-zinc-100 dark:text-zinc-900'
      : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-300 dark:hover:border-zinc-600 dark:hover:bg-zinc-900';
  }

  cardBackground(asset: BrandAsset): CardBackgroundMode {
    const override = this.cardBackgroundOverrides()[asset.key];
    if (override) {
      return override;
    }
    const global = this.globalBackground();
    if (global) {
      return global;
    }
    return defaultCardBackground(asset.variant, asset.asset);
  }

  setGlobalBackground(mode: CardBackgroundMode): void {
    this.globalBackground.set(mode);
    this.cardBackgroundOverrides.set({});
  }

  setCardBackground(asset: BrandAsset, mode: CardBackgroundMode): void {
    this.cardBackgroundOverrides.update((current) => ({
      ...current,
      [asset.key]: mode,
    }));
  }

  isGlobalBackgroundActive(mode: CardBackgroundMode): boolean {
    return this.globalBackground() === mode;
  }

  isCardBackgroundActive(asset: BrandAsset, mode: CardBackgroundMode): boolean {
    return this.cardBackground(asset) === mode;
  }

  previewSurfaceClass(asset: BrandAsset): string {
    const mode = this.cardBackground(asset);
    if (mode === 'dark') {
      return 'creatives-preview creatives-preview--dark';
    }
    if (mode === 'checkerboard') {
      return 'creatives-preview creatives-preview--checkerboard';
    }
    return 'creatives-preview creatives-preview--light';
  }

  previewSrc(asset: BrandAsset): string {
    const url = previewUrl(asset);
    const revision = this.assetRevisions()[asset.key];
    if (!revision || isVideoAsset(asset)) {
      return url;
    }
    const joiner = url.includes('?') ? '&' : '?';
    return `${url}${joiner}v=${revision}`;
  }

  requestDelete(asset: BrandAsset): void {
    this.pendingDeleteKey.set(asset.key);
    this.cardNotice.set(null);
  }

  cancelDelete(): void {
    this.pendingDeleteKey.set(null);
  }

  isDeletePending(asset: BrandAsset): boolean {
    return this.pendingDeleteKey() === asset.key;
  }

  isReplacing(asset: BrandAsset): boolean {
    return this.mutatingKey() === asset.key && this.mutationKind() === 'replace';
  }

  isDeleting(asset: BrandAsset): boolean {
    return this.mutatingKey() === asset.key && this.mutationKind() === 'delete';
  }

  cardNoticeFor(asset: BrandAsset): { tone: 'error' | 'success'; message: string } | null {
    const notice = this.cardNotice();
    if (!notice || notice.key !== asset.key) {
      return null;
    }
    return { tone: notice.tone, message: notice.message };
  }

  startReplace(asset: BrandAsset, input: HTMLInputElement): void {
    if (this.mutatingKey()) {
      return;
    }
    this.pendingDeleteKey.set(null);
    this.cardNotice.set(null);
    this.replaceTargetKey = asset.key;
    input.value = '';
    input.click();
  }

  onReplaceFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.item(0) ?? null;
    const key = this.replaceTargetKey;
    this.replaceTargetKey = null;
    input.value = '';
    if (!file || !key) {
      return Promise.resolve();
    }

    const asset = this.findAsset(key);
    if (!asset) {
      return Promise.resolve();
    }

    return this.replaceAsset(asset, file);
  }

  async confirmDelete(asset: BrandAsset): Promise<void> {
    if (this.mutatingKey()) {
      return;
    }

    this.mutatingKey.set(asset.key);
    this.mutationKind.set('delete');
    this.cardNotice.set(null);

    try {
      await this.brandingCreativesService.deleteCreative(asset.key);
      this.pendingDeleteKey.set(null);
      this.cardNotice.set({
        key: asset.key,
        tone: 'success',
        message: 'Removed from the library.',
      });
      this.loadLibrary();
    } catch (err: unknown) {
      this.cardNotice.set({
        key: asset.key,
        tone: 'error',
        message: this.readErrorMessage(err, 'Could not delete this file.'),
      });
    } finally {
      this.mutatingKey.set(null);
      this.mutationKind.set(null);
    }
  }

  private async replaceAsset(asset: BrandAsset, file: File): Promise<void> {
    if (!replacementExtensionMatches(file.name, asset.format)) {
      this.cardNotice.set({
        key: asset.key,
        tone: 'error',
        message: `Choose a .${asset.format} file so this URL stays the same.`,
      });
      return;
    }

    this.mutatingKey.set(asset.key);
    this.mutationKind.set('replace');
    this.cardNotice.set(null);

    try {
      await this.brandingCreativesService.replaceCreative(asset.key, file);
      this.assetRevisions.update((current) => ({
        ...current,
        [asset.key]: Date.now(),
      }));
      this.cardNotice.set({
        key: asset.key,
        tone: 'success',
        message: 'Replaced. The URL is unchanged.',
      });
      this.loadLibrary();
    } catch (err: unknown) {
      this.cardNotice.set({
        key: asset.key,
        tone: 'error',
        message: this.readErrorMessage(err, 'Could not replace this file.'),
      });
    } finally {
      this.mutatingKey.set(null);
      this.mutationKind.set(null);
    }
  }

  private findAsset(key: string): BrandAsset | null {
    for (const tab of this.tabs()) {
      const match = tab.assets.find((asset) => asset.key === key);
      if (match) {
        return match;
      }
    }
    return null;
  }

  previewAlt(asset: BrandAsset): string {
    if (isVideoAsset(asset)) {
      return `${asset.variant} video`;
    }
    return `${asset.brand} ${asset.asset} ${asset.variant}`;
  }

  backgroundModeLabel(mode: CardBackgroundMode): string {
    if (mode === 'checkerboard') {
      return 'Checker';
    }
    return mode.charAt(0).toUpperCase() + mode.slice(1);
  }

  async copyUrl(asset: BrandAsset): Promise<void> {
    try {
      await navigator.clipboard.writeText(asset.url);
      if (this.copiedResetTimer) {
        clearTimeout(this.copiedResetTimer);
      }
      this.copiedKey.set(asset.key);
      this.copiedResetTimer = setTimeout(() => {
        this.copiedKey.set(null);
        this.copiedResetTimer = null;
      }, 1600);
    } catch {
      this.copiedKey.set(null);
    }
  }

  isCopied(asset: BrandAsset): boolean {
    return this.copiedKey() === asset.key;
  }
}
